/**
 * CollaborationPanel - 协作面板组件
 * 显示 Deal 的成员列表和邀请入口
 */
import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, UserPlus, ChevronDown, ChevronUp, 
  Loader2, RefreshCw 
} from 'lucide-react'
import { DealMember, getDealMembers } from '../../services/member'
import { DealRole } from '../../services/invitation'
import MemberList from './MemberList'
import InviteMemberModal from './InviteMemberModal'
import { parseApiError } from '../../services/api'

interface CollaborationPanelProps {
  dealId: string
  dealName: string
  currentUserRole: DealRole
  className?: string
  defaultExpanded?: boolean
}

export default function CollaborationPanel({
  dealId,
  dealName,
  currentUserRole,
  className = '',
  defaultExpanded = true
}: CollaborationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isLoading, setIsLoading] = useState(true)
  const [members, setMembers] = useState<DealMember[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  
  const canInvite = currentUserRole === 'owner'
  
  const loadMembers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await getDealMembers(dealId)
      setMembers(data)
    } catch (err) {
      const apiError = parseApiError(err)
      setError(apiError.message)
    } finally {
      setIsLoading(false)
    }
  }, [dealId])
  
  useEffect(() => {
    loadMembers()
  }, [loadMembers])
  
  return (
    <div className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
      {/* 头部 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Users className="w-5 h-5 text-primary-600" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-gray-900">协作成员</h3>
            <p className="text-sm text-gray-500">
              {isLoading ? '加载中...' : `${members.length} 人`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canInvite && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowInviteModal(true)
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              邀请
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>
      
      {/* 内容 */}
      <motion.div
        initial={false}
        animate={{ 
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0
        }}
        transition={{ duration: 0.2 }}
        className="overflow-hidden"
      >
        <div className="px-4 pb-4">
          {/* 分隔线 */}
          <div className="border-t border-gray-100 mb-4" />
          
          {/* 加载状态 */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            </div>
          )}
          
          {/* 错误状态 */}
          {error && !isLoading && (
            <div className="text-center py-4">
              <p className="text-sm text-red-500 mb-2">{error}</p>
              <button
                onClick={loadMembers}
                className="flex items-center gap-1 mx-auto text-sm text-primary-600 hover:text-primary-700"
              >
                <RefreshCw className="w-4 h-4" />
                重试
              </button>
            </div>
          )}
          
          {/* 成员列表 */}
          {!isLoading && !error && (
            <MemberList
              members={members}
              currentUserRole={currentUserRole}
              onMemberUpdated={loadMembers}
            />
          )}
        </div>
      </motion.div>
      
      {/* 邀请弹窗 */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        dealId={dealId}
        dealName={dealName}
        onInviteSent={loadMembers}
      />
    </div>
  )
}

